import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { createCrudHooks } from '../../shared/api/crud-hooks';
import { employeesApi } from './api';
import type { EmployeeDto, EmployeeInput, EmployeeListItem, EmployeePasswordInput } from './types';

const QUERY_KEY = ['employees'] as const;

/** Hooks CRUD padrão (List, Get, Create, Update, Delete-demissão). */
export const employeesHooks = createCrudHooks<EmployeeListItem, EmployeeDto, EmployeeInput>({
  queryKey: QUERY_KEY,
  api: employeesApi,
});

/**
 * Hook das 4 ações de login. Invalida o cache de `employees` ao sucesso para que
 * a grid e o detalhe reflitam o novo `statusLogin`/`login` imediatamente.
 *
 * Sintomas conhecidos (memória `colaborador-status-via-data-demissao` + card [2KiMBypr]):
 * - `block` upstream tem bug: retorna 2xx mas não bloqueia (em investigação).
 *   Front pode desabilitar o botão até Genie corrigir.
 */
export function useEmployeeLoginHooks(): {
  useActivate: () => UseMutationResult<EmployeeDto, Error, number>;
  useBlock: () => UseMutationResult<EmployeeDto, Error, number>;
  useUnblock: () => UseMutationResult<EmployeeDto, Error, number>;
  usePassword: () => UseMutationResult<
    EmployeeDto,
    Error,
    { id: number; input: EmployeePasswordInput }
  >;
} {
  const qc = useQueryClient();
  const invalidate = (): Promise<void> =>
    qc.invalidateQueries({ queryKey: QUERY_KEY }).then(() => undefined);

  return {
    useActivate: () =>
      useMutation({
        mutationFn: (id) => employeesApi.loginActivate(id),
        onSuccess: invalidate,
      }),
    useBlock: () =>
      useMutation({
        mutationFn: (id) => employeesApi.loginBlock(id),
        onSuccess: invalidate,
      }),
    useUnblock: () =>
      useMutation({
        mutationFn: (id) => employeesApi.loginUnblock(id),
        onSuccess: invalidate,
      }),
    usePassword: () =>
      useMutation({
        mutationFn: ({ id, input }) => employeesApi.loginPassword(id, input),
        onSuccess: invalidate,
      }),
  };
}
